import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarBlank, PhoneCall, UserFocus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PACKAGES } from "@/lib/packages";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];

const SESSION_TYPES = [
  { id: "consultation", label: "30-Min Consultation", description: "A focused strategy call with Ciara.", icon: PhoneCall, flag: "credits" },
  { id: "paid_call", label: "1-on-1 Coaching Call (1 hr)", description: "Purchased call — deal analysis, rehab costs, live-deal walkthroughs. No refunds · 1 free reschedule.", icon: PhoneCall, flag: "calls" },
  { id: "one_on_one", label: "1-on-1 Coaching", description: "Private video call with Ciara — deal reviews, strategy, Q&A.", icon: UserFocus, flag: "oneOnOne" },
];

export default function Bookings() {
  const { user } = useAuth();
  const owned = user?.packages || (user?.package ? [user.package] : []);
  const canOneOnOne = owned.some((id) => PACKAGES.find((p) => p.id === id)?.oneOnOne);
  const canField = owned.some((id) => PACKAGES.find((p) => p.id === id)?.fieldTraining);
  const credits = user?.consultation_credits || 0;
  const callCredits = user?.call_credits || 0;
  const [rescheduling, setRescheduling] = useState(null);
  const [rDate, setRDate] = useState(null);
  const [rTime, setRTime] = useState(null);
  const [type, setType] = useState("one_on_one");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [notes, setNotes] = useState("");
  const [bookings, setBookings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [blocked, setBlocked] = useState([]);

  const loadBookings = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => {
    loadBookings();
    api.get("/bookings/blocked-dates").then((r) => setBlocked(r.data)).catch(() => {});
  }, []);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const today = new Date();
  const bookingWindowEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);

  const submit = async () => {
    if (!date || !time) {
      toast.error("Pick a date and time slot first.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", { type, date: format(date, "yyyy-MM-dd"), time, notes });
      toast.success("Session requested! Ciara will confirm shortly.");
      setDate(null);
      setTime(null);
      setNotes("");
      loadBookings();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled.");
      loadBookings();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitReschedule = async () => {
    if (!rDate || !rTime || !rescheduling) return;
    try {
      await api.post(`/bookings/${rescheduling.id}/reschedule`, { date: format(rDate, "yyyy-MM-dd"), time: rTime });
      toast.success("Call rescheduled — see you then.");
      setRescheduling(null);
      loadBookings();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const upcoming = bookings.filter((b) => b.status !== "cancelled");

  return (
    <AppShell>
      <div data-testid="bookings-page">
        <p className="text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Live Training</p>
        <h1 data-testid="bookings-heading" className="mt-3 font-heading text-4xl font-light sm:text-5xl">Book a session.</h1>

        {!canOneOnOne && !canField && credits < 1 && callCredits < 1 ? (
          <div className="mt-10 border border-[#2E2E2A] bg-[#1E1E1C] p-10" data-testid="bookings-locked">
            <h2 className="font-heading text-2xl font-light">Sessions unlock with a coaching package.</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#A3A39C]">
              1-on-1 coaching starts with the Full Flip Formula; in-the-field shadowing comes with the Inner Circle.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-px border border-[#2E2E2A] bg-[#2E2E2A] lg:grid-cols-2">
            {/* Left: type + calendar */}
            <div className="space-y-10 bg-[#1E1E1C] p-8">
              <div>
                <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">1 — Session type</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {SESSION_TYPES.map((st) => {
                    const allowed = st.flag === "credits" ? credits > 0 : st.flag === "calls" ? callCredits > 0 : st.flag === "fieldTraining" ? canField : canOneOnOne;
                    return (
                      <button
                        key={st.id}
                        data-testid={`session-type-${st.id}`}
                        disabled={!allowed}
                        onClick={() => setType(st.id)}
                        className={`border p-5 text-left transition-colors duration-200 ${
                          type === st.id
                            ? "border-[#C49A6C] bg-[#C49A6C]/10"
                            : "border-[#2E2E2A] hover:border-[#A3A39C]"
                        } ${!allowed ? "cursor-not-allowed opacity-40" : ""}`}
                      >
                        <st.icon size={24} weight="duotone" className="text-[#C49A6C]" />
                        <p className="mt-3 font-heading text-lg">{st.label}</p>
                        <p className="mt-1 text-xs leading-relaxed text-[#A3A39C]">
                          {allowed
                            ? st.flag === "credits"
                              ? `${st.description} ${credits} credit${credits === 1 ? "" : "s"} available.`
                              : st.flag === "calls"
                                ? `${st.description} ${callCredits} call${callCredits === 1 ? "" : "s"} available.`
                                : st.description
                            : st.flag === "credits"
                              ? "Requires a consultation purchase ($75) — see Menu"
                              : st.flag === "calls"
                                ? "Requires a purchased call ($125) — see Menu"
                                : `Requires ${st.flag === "fieldTraining" ? "Inner Circle" : "Full Flip Formula or Inner Circle"}`}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">2 — Pick a date</p>
                <p className="mb-4 text-xs text-[#A3A39C]">Availability opens two months at a time — the current month and next month.</p>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={[{ before: tomorrow }, ...blocked.map((d) => new Date(`${d}T12:00:00`))]}
                  fromMonth={today}
                  toMonth={bookingWindowEnd}
                  data-testid="booking-calendar"
                  className="border border-[#2E2E2A] bg-[#141413] text-[#F4F4F0]"
                />
              </div>
            </div>

            {/* Right: time + notes + submit */}
            <div className="flex flex-col bg-[#1E1E1C] p-8">
              <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">3 — Pick a time (ET)</p>
              <div className="grid grid-cols-4 gap-2" data-testid="time-slots">
                {TIME_SLOTS.map((t) => (
                  <button
                    key={t}
                    data-testid={`time-slot-${t.replace(":", "")}`}
                    onClick={() => setTime(t)}
                    className={`border py-3 text-sm transition-colors duration-200 ${
                      time === t
                        ? "border-[#C49A6C] bg-[#C49A6C] text-[#141413]"
                        : "border-[#2E2E2A] text-[#F4F4F0] hover:border-[#C49A6C]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <p className="mb-4 mt-10 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">4 — Notes for Ciara (optional)</p>
              <Textarea
                data-testid="booking-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What are you working on? Deals you want reviewed?"
                className="min-h-[120px] rounded-none border-[#2E2E2A] bg-[#141413] text-[#F4F4F0] placeholder:text-[#A3A39C]/60"
              />
              <Button
                data-testid="booking-submit-button"
                onClick={submit}
                disabled={submitting || !date || !time}
                className="mt-8 rounded-none bg-[#C49A6C] py-6 text-xs uppercase tracking-[0.15em] text-[#141413] transition-colors duration-200 hover:bg-[#F4F4F0] hover:text-[#141413]"
              >
                {submitting ? "Booking..." : "Request Session"}
              </Button>
            </div>
          </div>
        )}

        {/* Upcoming bookings */}
        <section className="mt-16" data-testid="my-bookings-section">
          <h2 className="mb-6 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Your Sessions</h2>
          {upcoming.length === 0 ? (
            <p className="border border-[#2E2E2A] bg-[#1E1E1C] p-8 text-sm text-[#A3A39C]" data-testid="no-bookings-message">
              No sessions booked yet.
            </p>
          ) : (
            <div className="divide-y divide-[#2E2E2A] border border-[#2E2E2A]">
              {upcoming.map((b) => (
                <div key={b.id} className="flex flex-wrap items-center gap-4 bg-[#1E1E1C] px-6 py-5" data-testid={`booking-row-${b.id}`}>
                  <CalendarBlank size={22} weight="duotone" className="text-[#C49A6C]" />
                  <div className="flex-1">
                    <p className="font-heading text-lg">
                      {b.type === "one_on_one" ? "1-on-1 Coaching" : b.type === "field_training" ? "In-the-Field Training" : b.type === "paid_call" ? "1-on-1 Coaching Call (1 hr)" : "30-Min Consultation"}
                    </p>
                    <p className="text-sm text-[#A3A39C]">{b.date} at {b.time} ET</p>
                  </div>
                  <span
                    data-testid={`booking-status-${b.id}`}
                    className={`border px-3 py-1 text-xs uppercase tracking-[0.15em] ${
                      b.status === "confirmed"
                        ? "border-[#C49A6C] text-[#C49A6C]"
                        : "border-[#2E2E2A] text-[#A3A39C]"
                    }`}
                  >
                    {b.status}
                  </span>
                  {b.type === "paid_call" ? (
                    b.rescheduled ? (
                      <span className="text-xs uppercase tracking-[0.15em] text-[#A3A39C]" data-testid={`booking-rescheduled-${b.id}`}>Rescheduled</span>
                    ) : (
                      <button
                        data-testid={`booking-reschedule-${b.id}`}
                        onClick={() => { setRescheduling(b); setRDate(null); setRTime(null); }}
                        className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#A3A39C] transition-colors duration-200 hover:text-[#C49A6C]"
                      >
                        <CalendarBlank size={14} /> Reschedule
                      </button>
                    )
                  ) : (
                    <button
                      data-testid={`booking-cancel-${b.id}`}
                      onClick={() => cancel(b.id)}
                      className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#A3A39C] transition-colors duration-200 hover:text-red-400"
                    >
                      <X size={14} /> Cancel
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <Dialog open={!!rescheduling} onOpenChange={() => setRescheduling(null)}>
          <DialogContent className="rounded-none border-[#2E2E2A] bg-[#1E1E1C] text-[#F4F4F0]" data-testid="reschedule-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-light">Reschedule your call</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[#A3A39C]">One free reschedule per call — no cancellations or refunds.</p>
            <Calendar
              mode="single"
              selected={rDate}
              onSelect={setRDate}
              disabled={[{ before: tomorrow }, ...blocked.map((d) => new Date(`${d}T12:00:00`))]}
              fromMonth={today}
              toMonth={bookingWindowEnd}
              className="border border-[#2E2E2A] bg-[#141413] text-[#F4F4F0]"
              data-testid="reschedule-calendar"
            />
            <div className="grid grid-cols-4 gap-2" data-testid="reschedule-slots">
              {TIME_SLOTS.map((t) => (
                <button
                  key={t}
                  data-testid={`reschedule-slot-${t.replace(":", "")}`}
                  onClick={() => setRTime(t)}
                  className={`border py-2 text-sm transition-colors duration-200 ${rTime === t ? "border-[#C49A6C] bg-[#C49A6C] text-[#141413]" : "border-[#2E2E2A] text-[#F4F4F0] hover:border-[#C49A6C]"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <Button
              data-testid="reschedule-confirm"
              onClick={submitReschedule}
              disabled={!rDate || !rTime}
              className="rounded-none bg-[#C49A6C] py-5 text-xs uppercase tracking-[0.15em] text-[#141413] hover:bg-[#F4F4F0]"
            >
              Confirm Reschedule
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
